import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

import {
  resetPasswordApi,
  resetPasswordAccountManagement,
} from '../../modules/Login/services';

// image imports
import logo from '../../utils/images/TagLogo_AllWhite.png';
import GenericSpinner from '../GenericSpinner/GenericSpinner';

interface IForgotPasswordProps {
  isForAccountManagement?: boolean;
  afterSubmissionSuccessful?: (data: unknown) => void;
  userId?: number;
}

const ForgotPassword: React.FC<IForgotPasswordProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const displayMobile = useMediaQuery('(max-width:959px)');
  const { enqueueSnackbar } = useSnackbar();

  const jwt = !props.isForAccountManagement
    ? location.search.split('=')[1]
    : '';

  const [isValidSubmission, setIsValidSubmission] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [retypeNewPassword, setRetypeNewPassword] = useState('');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    password: { error: false, msg: '' },
    retypePassword: { error: false, msg: '' },
  });

  const isBase64 = (str: string) => {
    if (str === '' || str.trim() === '') {
      return false;
    }
    try {
      return atob(btoa(str)) == str;
    } catch {
      return false;
    }
  };

  let apiCallForResetPassword: ((password: string, token: string, userId?: number) => Promise<{ status: number; data: unknown }>) | null = null;

  useEffect(() => {
    if (!props.isForAccountManagement) {
      if (jwt && isBase64(jwt)) {
        setIsValidSubmission(true);
      }
    } else {
      setIsValidSubmission(true);
    }
  }, [jwt, props.isForAccountManagement]);

  useEffect(() => {
    if (newPassword !== '' || retypeNewPassword !== '') {
      let newPasswordError = false;
      let newPasswordMsg = '';
      let retypePasswordError = false;
      let retypePasswordMsg = '';

      // password value
      if (
        !/(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/.test(
          newPassword
        ) ||
        newPassword.length === 0
      ) {
        newPasswordError = true;
        newPasswordMsg = 'Password requirements not met.';
      } else {
        newPasswordError = false;
        newPasswordMsg = '';
      }

      // do passwords match
      if (retypeNewPassword.length > 0 && newPassword !== retypeNewPassword) {
        retypePasswordError = true;
        retypePasswordMsg = 'Passwords must match.';
      } else {
        retypePasswordError = false;
        retypePasswordMsg = '';
      }

      setErrors({
        ...errors,
        password: { error: newPasswordError, msg: newPasswordMsg },
        retypePassword: { error: retypePasswordError, msg: retypePasswordMsg },
      });
    }
  }, [newPassword, retypeNewPassword]);

  const passwordInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      setNewPassword(value);
    } else {
      setRetypeNewPassword(value);
    }
  };

  const submitChangePasswordForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsFormSubmitted(true);

    const formData = new FormData(e.currentTarget);
    const newPwd = formData.get('newPassword') as string;
    const retypePwd = formData.get('retypeNewPassword') as string;

    const shouldResetPasswordRequestBeSubmitted =
      isValidSubmission && newPwd === retypePwd;

    if (shouldResetPasswordRequestBeSubmitted) {
      const apiCall = props.isForAccountManagement
        ? resetPasswordAccountManagement
        : resetPasswordApi;

      try {
        const response = await apiCall(newPwd, jwt, props.userId ?? 0);
        setIsFormSubmitted(false);
        if (response.status === 200) {
          const { data } = response;
          if (props.isForAccountManagement) {
            if (props.afterSubmissionSuccessful) {
              props.afterSubmissionSuccessful(data);
            }
          } else {
            navigate('/landing');
            enqueueSnackbar('Password reset!', { variant: 'success' });
          }
        }
      } catch (error: unknown) {
        setIsFormSubmitted(false);
        const parsedError = _.get(error, 'response.data.error') || error;
        enqueueSnackbar('Error: ' + parsedError, {
          variant: 'error',
        });
      }
    }
  };

  return (
    <Grid container spacing={0} alignItems="center" justifyContent="center">
      {isFormSubmitted && <GenericSpinner includeOverlay={true} padding={25} />}
      <Grid size={{ xs: 12, md: 12 }} className="pt-4 text-center">
        <img
          className="aft-logo"
          src={logo}
          alt="American Federation of Teachers Logo"
        />
      </Grid>
      <Grid size={12} className="pt-4 text-center">
        <span>Please enter your new password.</span>
      </Grid>
      <Grid size={{ xs: 12, md: 12 }} className="pt-2 text-center">
        <Typography variant="caption" gutterBottom>
          Password must contain one uppercase and one lowercase character, one
          number/special character, and at least 8 characters
        </Typography>
      </Grid>
      <Grid size={12} className="pt-2 text-center">
        <Typography variant="caption" gutterBottom>
          You cannot reuse your last password.
        </Typography>
      </Grid>
      <form
        method="post"
        onSubmit={submitChangePasswordForm}
        className={`pt-4 ${
          props.isForAccountManagement
            ? 'w-75'
            : displayMobile
            ? 'w-75'
            : 'w-25'
        }`}
      >
        <Grid>
          <TextField
            fullWidth
            id="new-password"
            label="Enter New Password"
            name="newPassword"
            type="password"
            required
            onChange={passwordInputOnChange}
            variant="outlined"
            helperText={errors.password.error ? errors.password.msg : ''}
            error={errors.password.error}
          />
        </Grid>
        <Grid className="pt-3">
          <TextField
            fullWidth
            id="retype-new-password"
            label="Retype New Password"
            name="retypeNewPassword"
            type="password"
            required
            onChange={passwordInputOnChange}
            variant="outlined"
            helperText={
              errors.retypePassword.error ? errors.retypePassword.msg : ''
            }
            error={errors.retypePassword.error}
          />
        </Grid>
        <Grid className="text-center pt-3">
          <Button
            className="btn"
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            disabled={errors.password.error || errors.retypePassword.error}
          >
            Reset Password
          </Button>
        </Grid>
      </form>
    </Grid>
  );
};

export default ForgotPassword;
